/**
 * useLocale — single source of truth for the user's locale preference.
 *
 * Order of precedence:
 *   1. localStorage `adjudon_locale` (explicit user choice)
 *   2. navigator.languages[0]
 *   3. navigator.language
 *   4. 'en-GB' fallback
 *
 * Returns:
 *   - `locale` — IETF tag, e.g. "de-DE" / "en-GB"
 *   - `lang` — short tag, e.g. "de" / "en"
 *   - `setLocale(loc)` — persist explicit choice (also dispatches a
 *     storage event so other tabs update)
 *
 * Used by: WelcomePage / TrustCenterPage / DPA-Accept modal to pick
 * which legal-doc locale to render.
 */

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'adjudon_locale';
const SUPPORTED_LANGS = ['de', 'en'] as const;
type SupportedLang = (typeof SUPPORTED_LANGS)[number];

function readStoredLocale(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

function detectLocale(): string {
  const stored = readStoredLocale();
  if (stored) return stored;
  if (typeof navigator !== 'undefined') {
    const langs = Array.isArray(navigator.languages) ? navigator.languages : null;
    if (langs && langs.length > 0 && typeof langs[0] === 'string' && langs[0].length > 0) {
      return langs[0];
    }
    if (typeof navigator.language === 'string' && navigator.language.length > 0) {
      return navigator.language;
    }
  }
  return 'en-GB';
}

export function useLocale() {
  const [locale, setLocaleState] = useState<string>(() => detectLocale());

  // Listen for changes from other tabs.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        setLocaleState(e.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const setLocale = useCallback((next: string) => {
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    setLocaleState(next);
  }, []);

  // Short language tag, normalised to a supported value for legal docs.
  const langPrimary = locale.split('-')[0]?.toLowerCase() ?? 'en';
  const lang: SupportedLang = (SUPPORTED_LANGS as readonly string[]).includes(langPrimary)
    ? (langPrimary as SupportedLang)
    : 'en';

  return { locale, lang, setLocale };
}
